// apps/codeframe/src/chrome/timeline/KeyframeLanes.tsx
import { useCanvasStore } from '../../store/canvas-store';
import { useTimelineStore } from './timeline-store';
import { timeToX, xToTime, snapTime } from './timeMap';
import { sampleValueAt } from './sampleValueAt';
import { ROW_H } from './TrackList';
import type { MotionTimeline, MotionTrack, MotionLayer, MotionChannel } from '../../store/types';

const TRACK_HEADER_H = 24; // matches TrackList track header (h-6)
const LAYER_HEADER_H = 20; // matches TrackList layer header (h-5)

export function KeyframeLanes({
  frameId,
  timeline,
  width,
}: {
  frameId: string;
  timeline: MotionTimeline;
  width: number;
}) {
  return (
    <div className="relative flex-1 overflow-x-hidden overflow-y-auto" style={{ width }}>
      {timeline.tracks.map((track) => (
        <div key={track.id} className="border-b border-[var(--color-chip-border)]">
          <div style={{ height: TRACK_HEADER_H }} />
          {track.layers.map((layer) => (
            <div key={layer.id}>
              <div style={{ height: LAYER_HEADER_H }} />
              {layer.channels.map((ch) => (
                <ChannelLane
                  key={ch.prop}
                  frameId={frameId}
                  timeline={timeline}
                  track={track}
                  layer={layer}
                  channel={ch}
                  width={width}
                />
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ChannelLane({
  frameId,
  timeline,
  track,
  layer,
  channel,
  width,
}: {
  frameId: string;
  timeline: MotionTimeline;
  track: MotionTrack;
  layer: MotionLayer;
  channel: MotionChannel;
  width: number;
}) {
  const dispatch = useCanvasStore((s) => s.dispatch);
  const selectKf = useTimelineStore((s) => s.selectKf);
  const openEasing = useTimelineStore((s) => s.openEasing);
  const selectedKf = useTimelineStore((s) => s.selectedKf);
  const dur = timeline.duration;

  const addAt = (clientX: number, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const t = snapTime(xToTime(clientX - rect.left, dur, width), timeline.fps);
    dispatch({
      t: 'motion.setKeyframe',
      frameId,
      trackId: track.id,
      layerId: layer.id,
      prop: channel.prop,
      kf: { t, value: sampleValueAt(timeline, track.id, layer.id, channel.prop, t), ease: 'easeInOut' },
    });
  };

  // Drag a diamond to retime it. A pointerup with no real movement (below
  // DRAG_THRESHOLD px) counts as a CLICK → opens the keyframe editor popover
  // at the cursor, targeting that keyframe.
  const DRAG_THRESHOLD = 3; // px
  const dragKf = (fromT: number, laneEl: HTMLElement, startX: number) => {
    const rect = laneEl.getBoundingClientRect();
    let lastT = fromT;
    let moved = false;
    const onMove = (ev: MouseEvent) => {
      if (!moved && Math.abs(ev.clientX - startX) > DRAG_THRESHOLD) moved = true;
      const t = snapTime(xToTime(ev.clientX - rect.left, dur, width), timeline.fps);
      if (t !== lastT) {
        dispatch({ t: 'motion.moveKeyframe', frameId, trackId: track.id, layerId: layer.id, prop: channel.prop, fromT: lastT, toT: t });
        lastT = t;
      }
    };
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (!moved) {
        // a click (not a drag) → edit this keyframe (value + ease) in the popover
        openEasing({ trackId: track.id, layerId: layer.id, prop: channel.prop, t: lastT }, ev.clientX, ev.clientY);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const kfs = channel.keyframes;

  return (
    <div
      className="relative border-b border-[var(--color-chip-border)]/40"
      style={{ height: ROW_H, width }}
      onDoubleClick={(e) => addAt(e.clientX, e.currentTarget)}
    >
      {/* tween segments (click → easing popover for the leading kf) */}
      {kfs.map((k, i) => {
        if (i === kfs.length - 1) return null;
        const x0 = timeToX(k.t, dur, width);
        const x1 = timeToX(kfs[i + 1].t, dur, width);
        return (
          <div
            key={`seg-${k.t}`}
            className="absolute top-1/2 h-px -translate-y-1/2 cursor-pointer bg-[var(--color-text-muted)]"
            style={{ left: x0, width: Math.max(0, x1 - x0) }}
            onClick={(e) => {
              e.stopPropagation();
              openEasing({ trackId: track.id, layerId: layer.id, prop: channel.prop, t: k.t }, e.clientX, e.clientY);
            }}
            title={`ease: ${typeof k.ease === 'string' ? k.ease : Object.keys(k.ease)[0]}`}
          />
        );
      })}
      {/* keyframe diamonds */}
      {kfs.map((k) => {
        const x = timeToX(k.t, dur, width);
        const sel =
          selectedKf &&
          selectedKf.trackId === track.id &&
          selectedKf.layerId === layer.id &&
          selectedKf.prop === channel.prop &&
          selectedKf.t === k.t;
        return (
          <div
            key={k.t}
            className={`absolute top-1/2 h-[9px] w-[9px] -translate-x-1/2 -translate-y-1/2 rotate-45 cursor-grab ${
              sel ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-text)]'
            }`}
            style={{ left: x }}
            title={`${(k.t / 1000).toFixed(2)}s = ${k.value}`}
            onMouseDown={(e) => {
              e.stopPropagation();
              selectKf({ trackId: track.id, layerId: layer.id, prop: channel.prop, t: k.t });
              dragKf(k.t, e.currentTarget.parentElement as HTMLElement, e.clientX);
            }}
          />
        );
      })}
    </div>
  );
}
